// app/javascript/scenes_editor/Interactions_manager.js

export function initInteractions(controller) {
  controller.interactionsByHotspotId = new Map()
  ;(controller.hotspots || []).forEach((hs) => {
    controller.interactionsByHotspotId.set(hs.id, [])
  })
}

export function getInteractions(controller, hsId) {
  if (!controller.interactionsByHotspotId.has(hsId)) {
    controller.interactionsByHotspotId.set(hsId, [])
  }
  return controller.interactionsByHotspotId.get(hsId)
}

export function ensureTmpInteractionsBucket(controller, tmpId) {
  if (!controller.interactionsByHotspotId.has(tmpId)) {
    controller.interactionsByHotspotId.set(tmpId, [])
  }
}

export function moveInteractionsBucket(controller, fromId, toId) {
  if (controller.interactionsByHotspotId.has(fromId)) {
    const list = controller.interactionsByHotspotId.get(fromId)
    controller.interactionsByHotspotId.delete(fromId)
    controller.interactionsByHotspotId.set(toId, list)
  } else {
    controller.interactionsByHotspotId.set(toId, [])
  }
}

export function deleteInteractionsBucket(controller, hsId) {
  controller.interactionsByHotspotId.delete(hsId)
}

export function updateInteractionsUIState(controller, hs) {
  if (!hs) return
  const isTmp = String(hs.id).startsWith("tmp_")

  const setDisabled = (btn, disabled) => {
    if (!btn) return
    btn.disabled = disabled
    btn.classList.toggle("opacity-50", disabled)
    btn.classList.toggle("cursor-not-allowed", disabled)

    if (disabled) btn.classList.remove("hover:bg-gray-50")
    else btn.classList.add("hover:bg-gray-50")
  }

  setDisabled(controller.hasBtnAddTextTarget ? controller.btnAddTextTarget : null, isTmp)
  setDisabled(controller.hasBtnAddQuizTarget ? controller.btnAddQuizTarget : null, isTmp)

  if (controller.hasInteractionsHintTarget) {
    controller.interactionsHintTarget.classList.toggle("hidden", !isTmp)
  }
}

export function loadInteractionsFromServer(controller, hsId) {
  if (!hsId) return
  if (String(hsId).startsWith("tmp_")) return
  if (!controller.interactionsIndexUrlTemplateValue) return

  const url = controller.interactionsIndexUrlTemplateValue.replace(":hotspot_id", hsId)

  fetch(url, { headers: { Accept: "application/json" } })
    .then((resp) => {
      if (!resp.ok) {
        return resp.text().then((txt) => {
          console.error("[EDITOR] interactions index failed", resp.status, txt)
          throw new Error("interactions index failed")
        })
      }
      return resp.json()
    })
    .then((data) => {
      const normalized = (data || []).map((it) => ({
        ...it,
        config: it.config || {},
        _open: false,
      }))

      controller.interactionsByHotspotId.set(hsId, normalized)
      renderInteractionsList(controller, hsId)
    })
    .catch((err) => {
      console.error("[EDITOR] loadInteractionsFromServer error", err)
    })
}

export function addTextInteraction(controller, e) {
  e?.preventDefault?.()

  const hs = controller.hotspots.find((h) => h.id === controller.selectedId)
  if (!hs) return
  if (String(hs.id).startsWith("tmp_")) return

  const items = getInteractions(controller, hs.id)
  const nextPosition = items.length ? Math.max(...items.map((i) => i.position || 0)) + 1 : 1

  items.push({
    id: `local_${Date.now()}`,
    kind: "tooltip",
    position: nextPosition,
    title: null,
    content: "",
    config: { mode: "indirect" }, // direct|indirect
    is_active: true,
    _open: true,
  })

  renderInteractionsList(controller, hs.id)
}

export function addQuizInteraction(controller, e) {
  e?.preventDefault?.()

  const hs = controller.hotspots.find((h) => h.id === controller.selectedId)
  if (!hs) return
  if (String(hs.id).startsWith("tmp_")) return

  const items = getInteractions(controller, hs.id)
  const nextPosition = items.length ? Math.max(...items.map((i) => i.position || 0)) + 1 : 1

  items.push({
    id: `local_${Date.now()}`,
    kind: "multiple_choice",
    position: nextPosition,
    title: null,
    content: null,
    config: {
      question: "",
      answers: [
        { id: `ans_${Date.now()}a`, text: "", correct: false },
        { id: `ans_${Date.now()}b`, text: "", correct: false },
      ],
    },
    is_active: true,
    _open: true,
  })

  renderInteractionsList(controller, hs.id)
}

export function renderInteractionsList(controller, hsId) {
  if (!controller.hasInteractionsListTarget) return

  const items = getInteractions(controller, hsId)
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))

  controller.interactionsListTarget.innerHTML = ""

  if (!items.length) {
    controller.interactionsListTarget.innerHTML =
      `<div class="hs-empty">Nessuna interazione ancora.</div>`
    return
  }

  const ensureMinTwoAnswers = (it) => {
    it.config = it.config || {}
    it.config.answers = Array.isArray(it.config.answers) ? it.config.answers : []
    while (it.config.answers.length < 2) {
      it.config.answers.push({
        id: `ans_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        text: "",
        correct: false,
      })
    }
  }

  items.forEach((it) => {
    const card = document.createElement("div")
    card.className = "hs-block"

    const header = document.createElement("button")
    header.type = "button"
    header.className = "hs-block__head w-full text-left"

    header.innerHTML = `
      <div class="hs-block__meta">
        <span class="hs-block__badge">${it.kind === "tooltip" ? "Testo" : "Quiz"}</span>
        <div class="hs-block__title">#${it.position} • ${it.kind === "tooltip" ? "Box testuale" : "Quiz"}</div>
      </div>
      <div class="text-xs text-gray-500">${it._open ? "Chiudi" : "Apri"}</div>
    `

    header.addEventListener("click", (e) => {
      e.preventDefault()
      it._open = !it._open
      renderInteractionsList(controller, hsId)
    })

    card.appendChild(header)

    if (it._open) {
      const body = document.createElement("div")
      body.className = "hs-block__body"

      if (it.kind === "tooltip") {
        it.config = it.config || {}
        const mode = it.config.mode || "indirect"

        const modeLabel = document.createElement("label")
        modeLabel.className = "block text-xs text-gray-600"
        modeLabel.textContent = "Tipo discorso"

        const modeSelect = document.createElement("select")
        modeSelect.className = "w-full rounded-lg border-gray-300"
        modeSelect.innerHTML = `
          <option value="indirect">Indiretto</option>
          <option value="direct">Diretto</option>
        `
        modeSelect.value = mode
        modeSelect.addEventListener("change", () => {
          it.config.mode = modeSelect.value
        })

        const textLabel = document.createElement("label")
        textLabel.className = "block text-xs text-gray-600"
        textLabel.textContent = "Testo"

        const textarea = document.createElement("textarea")
        textarea.rows = 4
        textarea.className = "w-full rounded-lg border-gray-300"
        textarea.value = it.content || ""
        textarea.addEventListener("input", () => {
          it.content = textarea.value
        })

        body.appendChild(modeLabel)
        body.appendChild(modeSelect)
        body.appendChild(textLabel)
        body.appendChild(textarea)
      }

      if (it.kind === "multiple_choice") {
        it.config = it.config || { question: "", answers: [] }
        ensureMinTwoAnswers(it)

        const qLabel = document.createElement("label")
        qLabel.className = "block text-xs text-gray-600"
        qLabel.textContent = "Domanda"

        const qInput = document.createElement("input")
        qInput.type = "text"
        qInput.className = "w-full rounded-lg border-gray-300"
        qInput.value = it.config.question || ""
        qInput.addEventListener("input", () => {
          it.config.question = qInput.value
        })

        body.appendChild(qLabel)
        body.appendChild(qInput)

        const answersTitle = document.createElement("div")
        answersTitle.className = "text-xs text-gray-600"
        answersTitle.textContent = "Risposte (spunta quelle vere)"

        const answersWrap = document.createElement("div")
        answersWrap.className = "space-y-2"

        it.config.answers.forEach((ans, idx) => {
          const block = document.createElement("div")
          block.className = "rounded-xl border p-3 bg-white/70"

          const ta = document.createElement("textarea")
          ta.rows = 1
          ta.className = "w-full border-0 p-0 focus:ring-0 resize-none text-sm"
          ta.placeholder = `Risposta ${idx + 1}`
          ta.value = ans.text || ""

          const autoResize = () => {
            ta.style.height = "auto"
            ta.style.height = `${ta.scrollHeight}px`
          }

          ta.addEventListener("input", () => {
            ans.text = ta.value
            autoResize()
          })

          setTimeout(autoResize, 0)

          const controls = document.createElement("div")
          controls.className = "mt-2 flex items-center justify-between"

          const truth = document.createElement("label")
          truth.className = "inline-flex items-center gap-2 text-xs text-gray-700"

          const chk = document.createElement("input")
          chk.type = "checkbox"
          chk.checked = !!ans.correct
          chk.addEventListener("change", () => {
            ans.correct = chk.checked
          })

          const truthText = document.createElement("span")
          truthText.textContent = "Vera"

          truth.appendChild(chk)
          truth.appendChild(truthText)

          const btnRemove = document.createElement("button")
          btnRemove.type = "button"
          btnRemove.className = "hs-icon-btn"
          btnRemove.textContent = "Rimuovi"

          btnRemove.disabled = it.config.answers.length <= 2
          btnRemove.classList.toggle("opacity-50", btnRemove.disabled)
          btnRemove.classList.toggle("cursor-not-allowed", btnRemove.disabled)

          btnRemove.addEventListener("click", () => {
            if (it.config.answers.length <= 2) return
            it.config.answers = it.config.answers.filter((a) => a.id !== ans.id)
            renderInteractionsList(controller, hsId)
          })

          controls.appendChild(truth)
          controls.appendChild(btnRemove)

          block.appendChild(ta)
          block.appendChild(controls)

          answersWrap.appendChild(block)
        })

        const btnAddAnswer = document.createElement("button")
        btnAddAnswer.type = "button"
        btnAddAnswer.className = "hs-pill-btn"
        btnAddAnswer.textContent = "+ Aggiungi risposta"
        btnAddAnswer.addEventListener("click", () => {
          it.config.answers.push({
            id: `ans_${Date.now()}_${Math.random().toString(16).slice(2)}`,
            text: "",
            correct: false,
          })
          renderInteractionsList(controller, hsId)
        })

        body.appendChild(answersTitle)
        body.appendChild(answersWrap)
        body.appendChild(btnAddAnswer)
      }

      const actions = document.createElement("div")
      actions.className = "hs-block__actions"

      const btnDel = document.createElement("button")
      btnDel.type = "button"
      btnDel.className = "hs-icon-btn"
      btnDel.style.color = "#b91c1c"
      btnDel.style.borderColor = "rgba(248,113,113,.28)"
      btnDel.style.background = "rgba(255,255,255,.72)"
      btnDel.textContent = "Elimina iterazione"
      btnDel.addEventListener("click", () => {
        const list = getInteractions(controller, hsId)
        const idx = list.findIndex((x) => x.id === it.id)
        if (idx >= 0) list.splice(idx, 1)

        const sorted = getInteractions(controller, hsId).sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        )
        sorted.forEach((x, i) => (x.position = i + 1))

        renderInteractionsList(controller, hsId)
      })

      actions.appendChild(btnDel)
      body.appendChild(actions)

      card.appendChild(body)
    }

    controller.interactionsListTarget.appendChild(card)
  })

}

export function buildInteractionsAttributes(controller, hsId) {
  const items = (controller.interactionsByHotspotId?.get(hsId) || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))

  // (opzionale ma consigliato) ignora quelle marcate per delete
  const filtered = items.filter((it) => !it?._destroy)

  return filtered.map((it, idx) => {
    const isLocal = String(it.id || "").startsWith("local_")

    return {
      ...(isLocal ? {} : { id: it.id }),

      kind: it.kind,
      position: idx + 1,
      content: it.kind === "tooltip" ? (it.content || "") : null,
      is_active: it.is_active !== false,
      config: it.config || {},
      // se in futuro gestisci delete “soft”:
      // _destroy: !!it._destroy,
    }
  })
}